/**
 * The add-players drawer's in-place filters panel (SE-6949, PC3 / SE-6950,
 * PC4).
 *
 * Tapping the drawer's Filters button replaces the drawer LIST with this
 * panel — same drawer, no second overlay. It offers the Scouting filter
 * vocabulary (Basic · Financial · Fit & Ratings · Performance · Goalkeeper ·
 * Report Stats) through the same shared machinery the scouting FilterSheet
 * runs on — `useFilterPanelLogic` over the panel-owned `useFilterState`,
 * mapped by the same `toFilterSheetSectionsProps` (SE-8608) — plus the
 * REASON and LISTS groups of the drawer's own.
 *
 * ## Draft semantics
 *
 * Selections here are a DRAFT: Show results commits them (and hands the
 * drawer its filtered-results view, PC7), Cancel discards them by closing —
 * the draft state lives in this subtree, and the panel is mounted only while
 * open, so unmount IS the discard. No reset effect required.
 *
 * ## The REASON group is client-side, deliberately
 *
 * Reasons exist only on suggestion candidates (`SuggestionReasonCategory`),
 * so the group's options are derived from the candidates in hand — the same
 * SE-5523 ruling the old in-list Reason dropdown followed — and the selection
 * is applied client-side over the suggestion pool by the results view.
 * @module features/player-kanban/components/organisms/AddPlayersFilterPanel
 */

import React, { useCallback, useMemo, useState } from "react";

import type { SuggestionReasonCategory } from "@/generated/graphql";
import type { FilterState } from "@/hooks/useFilterState";
import type { FilterOption } from "@/features/scouting/components/molecules/FilterChip";
import useFilterPanelLogic from "@/features/scouting/hooks/useFilterPanelLogic";
import { toFilterSheetSectionsProps } from "@/features/scouting/utils/toFilterSheetSectionsProps";
import { useAvailableCustomMetrics } from "@/hooks/useAvailableCustomMetrics";

import toggleId from "@/utils/toggleId";

import {
  toggleReason,
  type ReasonSelection,
} from "../../../utils/addPlayersDrawerControls";

import AddPlayersFilterPanelView from "./AddPlayersFilterPanelView";

/** One selectable REASON chip — a category the loaded candidates carry. */
export interface ReasonOption {
  /** The backend category. */
  readonly category: SuggestionReasonCategory;
  /** Product copy for the chip. */
  readonly label: string;
}

/** Props for the AddPlayersFilterPanel container. */
export interface AddPlayersFilterPanelContainerProps {
  /** The drawer's committed filter state, owned by the panel container. */
  readonly filterState: FilterState;
  /** The committed REASON selection, owned by the panel container. */
  readonly reasonSelection: ReasonSelection;
  /** REASON chip options, derived from the loaded suggestion candidates. */
  readonly reasonOptions: readonly ReasonOption[];
  /**
   * The committed LISTS selection — org watchlist ids the results are scoped
   * to (SE-7094).
   */
  readonly listSelection: readonly string[];
  /** LISTS options — the org's watchlists, as {id, label} (SE-7094). */
  readonly listOptions: readonly FilterOption[];
  /**
   * Commit the REASON draft. Fired by Show results, after the advanced draft
   * has been committed into `filterState`.
   */
  readonly onCommitReasons: (selection: ReasonSelection) => void;
  /** Commit the LISTS draft, alongside the REASON commit (SE-7094). */
  readonly onCommitLists: (selection: readonly string[]) => void;
  /** Leave the panel and return to the drawer's previous view. */
  readonly onClose: () => void;
}

/**
 * Drive the in-drawer filters panel.
 * @param root0 - Component props.
 * @param root0.filterState - Committed filter state.
 * @param root0.reasonSelection - Committed REASON selection.
 * @param root0.reasonOptions - REASON chip options.
 * @param root0.listSelection - Committed LISTS selection (SE-7094).
 * @param root0.listOptions - LISTS options (SE-7094).
 * @param root0.onCommitReasons - REASON commit callback.
 * @param root0.onCommitLists - LISTS commit callback (SE-7094).
 * @param root0.onClose - Close callback.
 */
const AddPlayersFilterPanelContainer = ({
  filterState,
  reasonSelection,
  reasonOptions,
  listSelection,
  listOptions,
  onCommitReasons,
  onCommitLists,
  onClose,
}: AddPlayersFilterPanelContainerProps) => {
  /*
   * The org's custom metrics drive the REPORT STATS section, exactly as they
   * do on the Watchlist (`AllTargetsTableContainer`). The id set also lets the
   * draft prune ranges for metrics the org no longer has.
   */
  const { availableCustomMetrics } = useAvailableCustomMetrics();
  const customMetrics = useMemo(
    () =>
      availableCustomMetrics.map(metric => ({
        id: metric.id,
        displayName: metric.displayName,
      })),
    [availableCustomMetrics]
  );
  const availableMetricIds = useMemo(
    () => new Set(availableCustomMetrics.map(metric => metric.id)),
    [availableCustomMetrics]
  );

  const panelLogic = useFilterPanelLogic({
    filterState,
    onClose,
    availableMetricIds,
  });

  /*
   * The REASON draft. Seeded from the committed selection at MOUNT — the
   * panel mounts when it opens and unmounts on Cancel/commit, so the seed is
   * always fresh and no reset effect is needed.
   */
  const [draftReasons, setDraftReasons] =
    useState<ReasonSelection>(reasonSelection);

  /* REASON opens expanded (PC3); the advanced groups open collapsed. */
  const [isReasonExpanded, setIsReasonExpanded] = useState(true);
  const onToggleReasonSection = useCallback(
    () => setIsReasonExpanded(expanded => !expanded),
    []
  );

  /**
   * REASON chip toggle for the RoleChipGrid-style grid — the id it hands back
   * is the `SuggestionReasonCategory` the option was built from.
   */
  const handleToggleReasonChip = useCallback(
    (id: string) =>
      setDraftReasons(current =>
        toggleReason(current, id as SuggestionReasonCategory)
      ),
    []
  );

  /** REASON options in the chip grid's `{id, label}` shape. */
  const reasonChipOptions = useMemo(
    () =>
      reasonOptions.map(option => ({
        id: option.category as string,
        label: option.label,
      })),
    [reasonOptions]
  );

  /*
   * The LISTS draft (SE-7094) — same lifecycle as the REASON draft: seeded
   * from the committed selection at mount, committed by Show results,
   * discarded by unmount.
   */
  const [draftListIds, setDraftListIds] =
    useState<readonly string[]>(listSelection);

  const handleToggleList = useCallback(
    (id: string) => setDraftListIds(current => toggleId(current, id)),
    []
  );
  const handleClearLists = useCallback(() => setDraftListIds([]), []);

  /* LISTS opens collapsed, like the advanced groups. */
  const [isListsExpanded, setIsListsExpanded] = useState(false);
  const onToggleListsSection = useCallback(
    () => setIsListsExpanded(expanded => !expanded),
    []
  );

  /*
   * The LISTS chip joins the shared one-open-dropdown mechanism through the
   * exposed setter, so opening it closes Positions/Leagues/Clubs and vice
   * versa.
   */
  const { setOpenChipId } = panelLogic;
  const handleToggleListChip = useCallback(
    () => setOpenChipId(prev => (prev === "list" ? null : "list")),
    [setOpenChipId]
  );

  const { clearDraft, commit, activeFilterCount } = panelLogic;

  const handleClearAll = useCallback(() => {
    clearDraft();
    setDraftReasons([]);
    setDraftListIds([]);
  }, [clearDraft]);

  const handleShowResults = useCallback(() => {
    commit();
    onCommitReasons(draftReasons);
    onCommitLists(draftListIds);
    onClose();
  }, [
    commit,
    draftListIds,
    draftReasons,
    onClose,
    onCommitLists,
    onCommitReasons,
  ]);

  const draftActiveCount =
    activeFilterCount + draftReasons.length + draftListIds.length;

  return (
    <AddPlayersFilterPanelView
      draftActiveCount={draftActiveCount}
      onClearAll={handleClearAll}
      onCancel={onClose}
      onShowResults={handleShowResults}
      /* --- Reason group --- */
      reasonChipOptions={reasonChipOptions}
      selectedReasons={draftReasons}
      onToggleReason={handleToggleReasonChip}
      isReasonExpanded={isReasonExpanded}
      onToggleReasonSection={onToggleReasonSection}
      /* --- Lists group (SE-7094) --- */
      isListsExpanded={isListsExpanded}
      onToggleListsSection={onToggleListsSection}
      listOptions={listOptions}
      selectedListIds={draftListIds}
      onToggleList={handleToggleList}
      onClearLists={handleClearLists}
      isListChipOpen={panelLogic.openChipId === "list"}
      onToggleListChip={handleToggleListChip}
      onCloseChip={panelLogic.onCloseChip}
      /* --- The Watchlist sections (SE-8608) — the same mapping the
         Watchlist FilterSheetContainer uses. --- */
      {...toFilterSheetSectionsProps(panelLogic, {
        positionOptions: filterState.positionOptions,
        clubOptions: filterState.clubOptions,
        nationalityOptions: filterState.nationalityOptions,
        leagueOptions: filterState.leagueOptions,
        roleOptions: filterState.roleOptions,
        optionsLoading: filterState.optionsLoading,
        onServerSearchClub: filterState.setClubSearchText,
        clubSearching: filterState.clubSearching,
        customMetrics,
      })}
    />
  );
};

AddPlayersFilterPanelContainer.displayName = "AddPlayersFilterPanelContainer";

export default AddPlayersFilterPanelContainer;
